import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Box,
  Chip,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiMapPin,
  HiEye,
  HiPencil,
  HiTrash,
  HiCurrencyRupee,
  HiCalendar,
  HiCheckCircle,
  HiClock,
  HiXCircle,
  HiSparkles,
} from "react-icons/hi2";
import AppPagination from "../Tools/AppPagination";
import api from "../../services/api";

export default function MyBikes() {
  const [bikes, setBikes] = useState([]);
  const [bikeId, setBikeId] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const customerId = localStorage.getItem("userId");

  useEffect(() => {
    loadBikes();
  }, [page]);

  const loadBikes = () => {
    api
      .get(`/usedBikes/customer?page=${page}`)
      .then((res) => {
        setBikes(res.data.usedBikes);
        setTotalPages(res.data.totalPages);
      })
      .catch(console.error);
  };

  const handleViewMore = (bikeId) =>
    navigate(`/usedBikeDetail?bikeId=${bikeId}`);
  const handleUpdate = (bikeId) => navigate(`/updateUsedBike?bikeId=${bikeId}`);

  const handleDelete = () => {
    api.delete(`/usedBikes/${customerId}/${bikeId}`).then(() => {
      setOpen(false);
      loadBikes();
      window.scrollTo(0, 0);
    });
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("active") || statusLower.includes("approved")) {
      return "success";
    } else if (statusLower.includes("pending") || statusLower.includes("waiting")) {
      return "warning";
    } else if (statusLower.includes("rejected") || statusLower.includes("inactive")) {
      return "error";
    }
    return "default";
  };

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("active") || statusLower.includes("approved")) {
      return <HiCheckCircle className="w-4 h-4" />;
    } else if (statusLower.includes("pending") || statusLower.includes("waiting")) {
      return <HiClock className="w-4 h-4" />;
    } else if (statusLower.includes("rejected") || statusLower.includes("inactive")) {
      return <HiXCircle className="w-4 h-4" />;
    }
    return <HiSparkles className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background with Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50">
        {/* Decorative Blobs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      {/* Sticky Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="sticky top-0 z-20 w-full"
      >
        <Card
          className="bg-white/90 backdrop-blur-sm shadow-md border-0 w-full"
          sx={{
            borderRadius: 0,
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
          }}
        >
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <div className="p-2 bg-gradient-to-r from-purple-500 to-indigo-500 shadow-md sm:rounded-lg">
                <HiSparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent whitespace-nowrap">
                My Bikes
              </h1>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">

        {/* Bikes Grid */}
        {bikes.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <HiSparkles className="w-12 h-12 text-purple-600" />
            </div>
            <Typography variant="h5" className="text-gray-700 mb-2">
              No bikes listed yet
            </Typography>
            <Typography variant="body1" className="text-gray-500">
              Start by adding your first bike listing
            </Typography>
          </motion.div>
        ) : (
          <Grid container spacing={4}>
            <AnimatePresence>
              {bikes.map((bike, index) => (
                <Grid item xs={12} sm={6} md={4} key={bike.bikeId}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card
                      className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 overflow-hidden h-full flex flex-col"
                      sx={{
                        borderRadius: "16px",
                        "&:hover": {
                          boxShadow: "0 25px 50px rgba(139, 92, 246, 0.15)",
                        },
                      }}
                    >
                      {/* Image Section */}
                      <div className="relative overflow-hidden">
                        <CardMedia
                          component="img"
                          height="240"
                          image={bike.imageUrl}
                          alt={bike.model}
                          className="cursor-pointer transition-transform duration-300 hover:scale-110"
                          onClick={() => handleViewMore(bike.bikeId)}
                        />
                        <div className="absolute top-4 right-4">
                          <Chip
                            icon={getStatusIcon(bike.displayStatus)}
                            label={bike.displayStatus}
                            color={getStatusColor(bike.displayStatus)}
                            size="small"
                            className="bg-white/90 backdrop-blur-sm shadow-md"
                          />
                        </div>
                      </div>

                      <CardContent className="flex-1 flex flex-col p-6">
                        {/* Bike Info */}
                        <div className="mb-4">
                          <Typography
                            variant="h6"
                            className="font-bold text-gray-900 mb-1"
                            sx={{ fontSize: "1.25rem" }}
                          >
                            {bike.brand} {bike.model}
                          </Typography>
                          <div className="flex items-center gap-4 text-gray-600 mb-3">
                            <div className="flex items-center gap-1">
                              <HiCalendar className="w-4 h-4" />
                              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                                {bike.modelYear}
                              </Typography>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mb-3">
                            <HiCurrencyRupee className="w-5 h-5 text-purple-600" />
                            <Typography
                              variant="h5"
                              className="font-bold text-purple-600"
                              sx={{ fontSize: "1.5rem" }}
                            >
                              {bike.price?.toLocaleString()}
                            </Typography>
                          </div>
                        </div>

                        {/* Location */}
                        <Box
                          display="flex"
                          alignItems="center"
                          className="mb-4 p-3 bg-gray-50 rounded-lg"
                        >
                          <HiMapPin className="w-5 h-5 text-purple-600 mr-2" />
                          <Typography
                            variant="body2"
                            className="text-gray-700"
                            sx={{ fontSize: "0.875rem" }}
                          >
                            {bike.sellerCity}
                            {bike.sellerAddress && `, ${bike.sellerAddress}`}
                          </Typography>
                        </Box>

                        {/* Action Buttons */}
                        <Box
                          display="flex"
                          gap={1}
                          mt="auto"
                          className="flex-wrap"
                        >
                          <Button
                            variant="contained"
                            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg flex-1 min-w-[80px]"
                            startIcon={<HiEye className="w-4 h-4" />}
                            onClick={() => handleViewMore(bike.bikeId)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              boxShadow: "0 4px 14px rgba(139, 92, 246, 0.3)",
                              "&:hover": {
                                boxShadow: "0 6px 20px rgba(139, 92, 246, 0.4)",
                              },
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="contained"
                            className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white rounded-lg flex-1 min-w-[80px]"
                            startIcon={<HiPencil className="w-4 h-4" />}
                            onClick={() => handleUpdate(bike.bikeId)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              boxShadow: "0 4px 14px rgba(99, 102, 241, 0.3)",
                              "&:hover": {
                                boxShadow: "0 6px 20px rgba(99, 102, 241, 0.4)",
                              },
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="contained"
                            className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg flex-1 min-w-[80px]"
                            startIcon={<HiTrash className="w-4 h-4" />}
                            onClick={() => {
                              setBikeId(bike.bikeId);
                              setOpen(true);
                            }}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              boxShadow: "0 4px 14px rgba(239, 68, 68, 0.3)",
                              "&:hover": {
                                boxShadow: "0 6px 20px rgba(239, 68, 68, 0.4)",
                              },
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        )}

        {/* Pagination */}
        <Box mt={6} mb={10} display="flex" justifyContent="center">
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <AppPagination setPage={setPage} totalPages={totalPages} />
            </motion.div>
          )}
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              color: "white",
              fontWeight: 600,
              fontSize: "1.25rem",
            }}
          >
            Delete Bike?
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <DialogContentText sx={{ fontSize: "1rem", color: "#374151" }}>
              Are you sure you want to delete this bike? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={() => setOpen(false)}
              className="rounded-lg"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#6b7280",
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "0 4px 14px rgba(239, 68, 68, 0.3)",
                "&:hover": {
                  boxShadow: "0 6px 20px rgba(239, 68, 68, 0.4)",
                },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}
